import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import ModalWrapper, { ModalContent } from "./ModalWrapper";

const Menu = React.forwardRef(
  (
    {
      elevation,
      className,
      anchor, // Legacy CSS string anchor
      anchorEl, // New explicit DOM node or React ref anchor
      align = "bottom",
      style,
      children,
      animate,
      delay,
      zIndex,
      noScrim,
      transparent,
      onAfterAnimate,
      onClose,
      ...rest
    },
    forwardedRef,
  ) => {
    const classes = [
      "ta-menu",
      elevation && "ta-elevation--shadow ta-elevation_" + elevation,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const localRef = useRef(null);
    const markerRef = useRef(null);
    useImperativeHandle(forwardedRef, () => localRef.current);

    const [coords, setCoords] = useState(null);

    useEffect(() => {
      if (!animate) {
        setCoords(null);
        return;
      }

      let resizeObserver = null;
      let rafId = null;

      const updatePosition = () => {
        let targetAnchor = null;

        if (anchorEl) {
          // Allow passing a React Ref or a direct DOM element
          targetAnchor = anchorEl.current || anchorEl;
        } else if (anchor) {
          // Fallback to legacy hacky CSS selector
          targetAnchor = document.querySelector(`[style*="${anchor}"]`);
        } else if (markerRef.current) {
          // Auto-detect previous sibling in the DOM tree!
          targetAnchor =
            markerRef.current.previousElementSibling ||
            markerRef.current.parentElement;
        }

        const menuEl = localRef.current;

        // ModalWrapper has a delay before it mounts the children, so we loop until it exists
        if (!targetAnchor || !menuEl) {
          rafId = requestAnimationFrame(updatePosition);
          return;
        }

        // Attach observer once mounted so we track any CSS transition size expansions (like max-height: 0 -> full)
        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(() => updatePosition());
          resizeObserver.observe(menuEl);
          resizeObserver.observe(targetAnchor);
        }

        const anchorRect = targetAnchor.getBoundingClientRect();
        const menuRect = menuEl.getBoundingClientRect();

        let left = 0;
        let top = 0;
        const margin = 4;

        if (align === "bottom" || !align) {
          left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;
          top = anchorRect.bottom + margin;
        } else if (align === "top") {
          left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;
          top = anchorRect.top - menuRect.height - margin;
        } else if (align === "left") {
          left = anchorRect.left - menuRect.width - margin;
          top = anchorRect.top + anchorRect.height / 2 - menuRect.height / 2;
        } else if (align === "right") {
          left = anchorRect.right + margin;
          top = anchorRect.top + anchorRect.height / 2 - menuRect.height / 2;
        }

        // Clamp to viewport
        if (left + menuRect.width > window.innerWidth) {
          left = window.innerWidth - menuRect.width - margin;
        }
        if (left < 0) left = margin;
        if (top + menuRect.height > window.innerHeight) {
          top = window.innerHeight - menuRect.height - margin;
        }
        if (top < 0) top = margin;

        setCoords({ left: left + window.scrollX, top: top + window.scrollY });
      };

      rafId = requestAnimationFrame(updatePosition);

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (resizeObserver) resizeObserver.disconnect();
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }, [animate, anchor, anchorEl, align]);

    return (
      <>
        <span ref={markerRef} style={{ display: "none" }} />
        <ModalWrapper
          animate={animate}
          delay={delay}
          zIndex={zIndex}
          noScrim={noScrim}
          transparent={transparent}
          onAfterAnimate={onAfterAnimate}
          onClose={onClose}
          portal={true}
        >
          <div
            ref={localRef}
            className={classes}
            data-align={align || "bottom"}
            style={{
              ...style,
              ...(coords
                ? {
                    position: "absolute",
                    left: coords.left + "px",
                    top: coords.top + "px",
                    margin: 0,
                    positionAnchor: "none",
                    positionArea: "none",
                    insetArea: "none",
                  }
                : {
                    positionAnchor: anchor || "--menu-anchor",
                    visibility: animate ? "hidden" : undefined,
                  }),
            }}
            {...rest}
          >
            <ModalContent>{children}</ModalContent>
          </div>
        </ModalWrapper>
      </>
    );
  },
);

export const MenuWrapper = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const childrenArray = React.Children.toArray(children);
  if (childrenArray.length !== 2) {
    console.warn(
      "MenuWrapper requires exactly 2 children: a trigger and a Menu component",
    );
    return <>{children}</>;
  }

  const trigger = childrenArray[0];
  const menu = childrenArray[1];

  return (
    <>
      {React.cloneElement(trigger, {
        ref: triggerRef,
        onClick: (e) => {
          setIsOpen((prev) => !prev);
          if (trigger.props.onClick) trigger.props.onClick(e);
        },
        selected: isOpen || trigger.props.selected, // Highlights the trigger button while menu is open
      })}
      {React.cloneElement(menu, {
        animate: isOpen,
        onClose: () => setIsOpen(false),
        onAfterAnimate: () => setIsOpen(false),
        anchorEl: triggerRef,
      })}
    </>
  );
};

export default Menu;
